import { useState } from "react";
import { Star } from "lucide-react";
import { Button } from "~/components/ui/button";
import { cn } from "~/lib/utils";
import { api } from "~/utils/api";
import { useUser } from "~/modules/user/hooks/useUser";
import { useToaster } from "~/modules/toaster/hooks/useToaster";
import { Chart } from "~/modules/charts/types";
import { track } from "~/modules/analytics/track";

// // // //

/** Compact star-count formatter, e.g. 1280 → "1.3k". */
const compactCount = new Intl.NumberFormat("en-US", {
    notation: "compact",
    maximumFractionDigits: 1,
});

type Props = {
    chart: Chart;
    /**
     * "button" renders a labeled action button; "icon" renders a compact star;
     * "count" renders the inline star + favorite count used on list cards.
     */
    variant?: "button" | "icon" | "count";
    addFavoriteAnalyticsLabel: string;
    removeFavoriteAnalyticsLabel: string;
};

/**
 * ChartFavoriteButton
 * Toggles whether the current user has starred an chart. The favorite state
 * and count update optimistically on click; the optimistic override is cleared
 * once the server responds (success refetches the chart queries, error reverts).
 */
export function ChartFavoriteButton({
    chart,
    variant = "button",
    addFavoriteAnalyticsLabel,
    removeFavoriteAnalyticsLabel,
}: Props) {
    const { user, isLoggedIn } = useUser();
    const { addToaster } = useToaster();

    // Optimistic override of the prop's favorite state/count while a toggle is
    // in flight. `null` means "fall back to the server-provided chart".
    const [optimistic, setOptimistic] = useState<{
        isFavorite: boolean;
        favoriteCount: number;
    } | null>(null);

    const setFavorite = api.charts.setFavorite.useMutation({
        onError: (error) => {
            setOptimistic(null);
            addToaster({
                variant: "danger",
                message: error.message || "Error updating favorite.",
            });
        },
    });

    // Favoriting requires an account, so hide the control for logged-out users.
    if (!isLoggedIn) {
        return null;
    }

    const isFavorite = optimistic?.isFavorite ?? chart.isFavorite;
    const favoriteCount = optimistic?.favoriteCount ?? chart.favoriteCount;

    const handleToggle = (e: React.MouseEvent) => {
        // Cards wrap the button in a link — don't navigate when starring.
        e.preventDefault();
        e.stopPropagation();
        const favorite = !isFavorite;
        setOptimistic({
            isFavorite: favorite,
            favoriteCount: Math.max(
                0,
                chart.favoriteCount + (favorite ? 1 : -1),
            ),
        });

        track(
            favorite ? addFavoriteAnalyticsLabel : removeFavoriteAnalyticsLabel,
            {
                chart_slug: chart.slug,
                chart_type: chart.type,
            },
        );

        setFavorite.mutate({
            id: chart.id,
            organizationID: user.organization.id,
            favorite,
        });
    };

    const star = (
        <Star
            className={cn(
                "size-5",
                isFavorite && "fill-yellow-400 text-yellow-400",
            )}
        />
    );

    if (variant === "count") {
        return (
            <button
                type="button"
                onClick={handleToggle}
                disabled={setFavorite.isLoading}
                aria-pressed={isFavorite}
                aria-label={
                    isFavorite ? "Remove from favorites" : "Add to favorites"
                }
                className="relative z-10 -mx-1 inline-flex items-center gap-1 rounded-md px-1 py-0.5 text-muted-foreground transition-colors hover:bg-yellow-50 disabled:opacity-50"
            >
                <Star
                    className={cn(
                        "size-3.5",
                        isFavorite
                            ? "fill-yellow-400 text-yellow-400"
                            : "text-yellow-500/70",
                    )}
                />
                {favoriteCount > 0 && compactCount.format(favoriteCount)}
            </button>
        );
    }

    if (variant === "icon") {
        return (
            <Button
                type="button"
                variant="ghost"
                size="icon"
                onClick={handleToggle}
                disabled={setFavorite.isLoading}
                aria-pressed={isFavorite}
                aria-label={
                    isFavorite ? "Remove from favorites" : "Add to favorites"
                }
            >
                {star}
            </Button>
        );
    }

    return (
        <Button
            type="button"
            variant="outline"
            className={cn(
                "gap-1.5 text-base",
                isFavorite &&
                    "border-yellow-300 bg-yellow-50 text-yellow-700 hover:bg-yellow-100",
            )}
            onClick={handleToggle}
            disabled={setFavorite.isLoading}
            aria-pressed={isFavorite}
            aria-label={
                isFavorite ? "Remove from favorites" : "Add to favorites"
            }
        >
            {star}
            {favoriteCount > 0 && compactCount.format(favoriteCount)}
        </Button>
    );
}
